import type { CSSProperties } from "react";
import { alpha, type Theme } from "@mui/material/styles";

/**
 * Reusable style recipes for the Bestek AI design language.
 *
 * These are attached to the theme (`theme.mixins.*`) and read every colour from
 * `theme.palette` / `theme.customShadows` — they hold no colour values of their
 * own. Only geometry and motion live here.
 */

export const RADIUS = {
  card: 16,
  cardLg: 18,
  tile: 14,
  button: 10,
  input: 10,
  inputSm: 9,
  pill: 999,
} as const;

export const TRANSITION = "all 150ms cubic-bezier(0.2,0,0,1)";

export type StatusKind = "completed" | "running" | "failed" | "queued";

export interface DesignMixins {
  iconBox: (size?: number, radius?: number) => CSSProperties;
  statusChip: (status: StatusKind) => CSSProperties;
  sevBadge: (sev?: string) => CSSProperties;
  primaryButton: (extra?: CSSProperties) => CSSProperties;
}

declare module "@mui/material/styles" {
  interface Mixins extends DesignMixins {}
  interface MixinsOptions extends Partial<DesignMixins> {}
}

const pill: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  padding: "3px 10px",
  borderRadius: RADIUS.pill,
};

export function createDesignMixins(theme: Theme): DesignMixins {
  const { palette, customShadows } = theme;
  const { brand, surface, status } = palette;

  return {
    iconBox: (size = 42, radius = 11) => ({
      width: size,
      height: size,
      flex: "none",
      borderRadius: radius,
      background: surface.cyan,
      color: brand.icon,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),

    statusChip: (kind) => {
      // "running" (in-progress/processing) uses a soft-primary treatment — same
      // formula as the MuiChip "soft" variant + color="primary" override
      // (theme/overrides/components/chip.tsx) — rather than the status palette.
      const map: Record<StatusKind, [string, string]> = {
        completed: [status.successBg, status.successFg],
        running: [alpha(palette.primary.main, 0.16), palette.primary.dark],
        failed: [status.failBg, status.failFg],
        queued: [status.neutralBg, status.neutralFg],
      };
      const [bg, fg] = map[kind] || map.completed;
      return {
        background: bg,
        color: fg,
      };
    },

    sevBadge: (sev) => {
      const s = (sev || "").toLowerCase();
      let bg: string = status.neutralBg;
      let fg: string = status.neutralFg;
      if (s.indexOf("hoog") >= 0) {
        bg = status.failBg;
        fg = status.failFg;
      } else if (s.indexOf("gemiddeld") >= 0 || s.indexOf("midden") >= 0) {
        bg = status.warnBg;
        fg = status.warnFg;
      } else if (s.indexOf("laag") >= 0) {
        bg = status.successBg;
        fg = status.successFg;
      }
      return { background: bg, color: fg };
    },

    // Ink is `primary.contrastText` (navy), not white — white on the cyan
    // primary was 1.62:1 and failed WCAG 1.4.3 (UI/UX audit, §3).
    primaryButton: (extra) => ({
      display: "flex",
      alignItems: "center",
      gap: 1,
      background: palette.primary.main,
      color: palette.primary.contrastText,
      border: "none",
      borderRadius: `${RADIUS.button}px`,
      padding: "11px 18px",
      fontFamily: "inherit",
      fontSize: 14,
      fontWeight: 700,
      cursor: "pointer",
      boxShadow: customShadows.buttonCyan,
      transition: "background 150ms,transform 150ms",
      ...extra,
    }),
  };
}
